import $Input from 'formik-antd/lib/input';
import $Select from 'formik-antd/lib/select';
import Switch from 'formik-antd/lib/switch';
import { AutoComplete } from 'formik-antd/lib/auto-complete';
import { TFunction } from 'i18next';
import {
  Action,
  APIAsset,
  Flex,
  FormGroup,
  Grid,
  Operator,
  useCustodianQuery,
} from '@mss/mss-component-library';
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AutomationAsset,
  AutomationsContext,
  ServicenowConfig,
} from './AutomationForm';
import { faPlus } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ErrorMessage, Field, useFormikContext } from 'formik';
import CustodianSelect from 'jsx/asset/components/CustodianSelect';
import get from 'lodash/get';
import { JsonSchemaFormComponent } from './JsonSchemaForm';
import { Button, Input as AntdInput, Tag, Tooltip } from 'antd';
import { Grid as GridIcon } from 'react-feather';
import { FormValuesContext } from '../contexts/FormValuesContext';
import moment from 'moment';
import debounce from 'lodash/debounce';

import {
  searchForAssetsText,
  useAllAssetsText,
  useIdFromTriggerText,
} from '../util/utils';
import classNames from 'classnames';

type AutomationType = 'trigger' | 'condition' | 'action';

const templateString = (
  type: AutomationType,
  index?: number,
  nestedAt?: number
) =>
  `${type}${index != null ? `s.${nestedAt ?? index}` : ''}${
    nestedAt != null ? `.conditions.${index}` : ''
  }`;

export const Select = ({
  className,
  ...props
}: React.ComponentProps<typeof $Select>) => {
  return (
    <$Select
      className={classNames({ 'w-full': !className }, className)}
      {...props}
    />
  );
};

Select.Option = $Select.Option;
Select.OptGroup = $Select.OptGroup;

export const Input = ({
  className,
  ...props
}: React.ComponentProps<typeof $Input>) => {
  return (
    <$Input
      className={classNames({ 'w-full': !className }, className)}
      {...props}
    />
  );
};

Input.TextArea = $Input.TextArea;
Input.Password = $Input.Password;

const TIMEPATTERN_ERROR = 'Time / Time pattern cannot be zero';

const emptyFieldValidation = (value: any) => {
  let error;
  if (value !== 0 && !value) {
    error = 'Required';
  }
  return error;
};

const timePatternValidation = (
  hours: number,
  minutes: number,
  seconds: number,
  required: boolean
) => {
  let error;
  if ((hours == null || minutes == null || seconds == null) && required) {
    error = 'Required';
  } else if (hours === 0 && minutes === 0 && seconds === 0) {
    error = TIMEPATTERN_ERROR;
  }

  return error;
};

export type NestedSelectorProps = {
  action?: Action;
  index?: number;
  type: AutomationType;
  required?: boolean;
  valueKey?: string;
  header?: string;
  nestedAt?: number;
  canInherit?: boolean;
  isTimePattern?: boolean;
  attributesOptions?: [] | undefined | null;
  sendToCustodians?: (check: boolean) => void;
  onSelectedServiceChange?: (service: string) => void;
  isTime?: boolean;
  isAction?: boolean;
  disabled: string | boolean;
  subtype?: 'incident' | 'case';
};

export const MssIdSelector = ({
  action,
  index,
  type,
  nestedAt,
  isTime,
  isTimePattern,
  isAction,
  disabled,
  required,
}: NestedSelectorProps) => {
  const { assetsSelectDataSource, assetToSearch, setAssetToSearch, assets } =
    useContext(AutomationsContext);

  const { setFieldValue, getFieldProps, values } = useFormikContext<any>();

  const valuePath = `${templateString(
    type,
    index,
    nestedAt
  )}.settings.asset_id`;

  const isActionEmpty = action?.settings.asset_id === useAllAssetsText;
  const isEmpty = getFieldProps(valuePath).value === useAllAssetsText;

  const [allApplicableAssets, setAllApplicableAssets] = useState(isEmpty);
  const buttonRef = useRef<HTMLHeadingElement>(null);

  const [t] = useTranslation(['configuration', 'common']);

  useEffect(() => {
    if (isTime || isTimePattern) {
      setAllApplicableAssets(false);

      if (isActionEmpty) {
        setFieldValue(valuePath, null);
      }
    }
  }, [isTime, isTimePattern]);

  const onSearch = (value: string) => setAssetToSearch(value);

  const handleAllButtonClick = () => {
    const buttonState = !allApplicableAssets;

    if (buttonState) {
      setFieldValue(valuePath, useAllAssetsText);
      setAllApplicableAssets(true);
    } else {
      setFieldValue(valuePath, null);
      setAllApplicableAssets(false);
    }
  };

  const getPlaceholder = () => {
    if (isAction) {
      if (allApplicableAssets) {
        return useIdFromTriggerText;
      } else {
        return searchForAssetsText;
      }
    } else {
      if (allApplicableAssets) {
        return useAllAssetsText;
      } else {
        return searchForAssetsText;
      }
    }
  };

  const getTooltipTitle = () => {
    if (isAction) {
      if (allApplicableAssets) {
        return searchForAssetsText;
      } else {
        return useIdFromTriggerText;
      }
    } else {
      if (allApplicableAssets) {
        return searchForAssetsText;
      } else {
        return useAllAssetsText;
      }
    }
  };

  const currentValue = get(
    values,
    `${templateString(type, index, nestedAt)}.settings.asset_id`
  );
  const buttonTitle = isAction ? 'Use from trigger' : 'All';
  const buttonWidth = buttonRef.current?.offsetWidth ?? 0;

  return (
    <FormGroup
      label={t('Asset / Application (mss.id)')}
      required={required}
      input={
        <AntdInput.Group compact>
          <Select
            disabled={!!disabled || allApplicableAssets}
            validate={required ? emptyFieldValidation : undefined}
            showSearch={true}
            onSearch={debounce(onSearch, 300)}
            showArrow={false}
            filterOption={false}
            notFoundContent={null}
            data-ta={'Triggers_AssetApplication'}
            name={`${templateString(type, index, nestedAt)}.settings.asset_id`}
            placeholder={getPlaceholder()}
            className="m-0"
            style={{
              width: `calc(100% - ${buttonWidth ? buttonWidth + 1 : 0}px)`,
            }}
          >
            {assets &&
              currentValue &&
              !assetToSearch &&
              assetsSelectDataSource.findIndex(x => x.id === currentValue) <
                0 && (
                <Select.Option key={currentValue} value={currentValue}>
                  {assets.find(
                    (a: APIAsset | AutomationAsset) => a.id === currentValue
                  )?.name ||
                    currentValue ||
                    'empty'}
                </Select.Option>
              )}
            {assetToSearch &&
              assetsSelectDataSource.map(
                (asset: APIAsset | AutomationAsset) => (
                  <Select.Option key={asset.id || 'empty'} value={asset.id}>
                    {asset.name}
                  </Select.Option>
                )
              )}
          </Select>

          {!isTime && !isTimePattern && (
            <Tooltip title={getTooltipTitle()}>
              <Button
                disabled={!!disabled}
                ref={buttonRef}
                data-ta="allApplicableAssetsButton"
                onClick={handleAllButtonClick}
              >
                <GridIcon
                  size={14}
                  className="mr-1 mb-1"
                  fill={
                    allApplicableAssets
                      ? 'rgba(94, 140, 140, 1)'
                      : 'rgba(94, 140, 140, 0.1)'
                  }
                />
                {t(buttonTitle)}
              </Button>
            </Tooltip>
          )}
        </AntdInput.Group>
      }
    />
  );
};
type OperatorSelect = {
  key: string;
  value: Operator;
};

const fullOperators = (t: TFunction): OperatorSelect[] => [
  { key: t('Greater than (>)'), value: '>' },
  { key: t('Greater than or equal to (>=)'), value: '>=' },
  { key: t('Smaller than (<)'), value: '<' },
  { key: t('Smaller than or equal to (<=)'), value: '<=' },
  { key: t('Equal to (==)'), value: '==' },
  { key: t('Not equal to (!=)'), value: '!=' },
];

const simpleOperators = (t: TFunction): OperatorSelect[] => [
  { key: t('Equal to (==)'), value: '==' },
  { key: t('Not equal to (!=)'), value: '!=' },
  { key: t('In list (in)'), value: 'in' },
];

export const OperatorSelector = (props: NestedSelectorProps) => (
  <BaseOperatorSelector {...props} operators={fullOperators} />
);
export const SimpleOperatorSelector = (props: NestedSelectorProps) => (
  <BaseOperatorSelector {...props} operators={simpleOperators} />
);

export const BaseOperatorSelector = ({
  operators,
  required,
  type,
  index,
  nestedAt,
  disabled,
}: NestedSelectorProps & { operators: (t: TFunction) => OperatorSelect[] }) => {
  const [t] = useTranslation(['configuration', 'common']);
  return (
    <FormGroup
      required={required}
      label={t('Operator')}
      input={
        <Select
          disabled={!!disabled}
          validate={required ? emptyFieldValidation : undefined}
          data-ta="Triggers_Operator"
          name={`${templateString(type, index, nestedAt)}.settings.operator`}
        >
          {operators(t).map(o => (
            <Select.Option key={o.value} value={o.value}>
              {o.key}
            </Select.Option>
          ))}
        </Select>
      }
    />
  );
};

export const PrioritySelect = ({
  required,
  type,
  index,
  nestedAt,
  disabled,
}: NestedSelectorProps) => {
  const [t] = useTranslation(['configuration', 'common']);
  return (
    <FormGroup
      required={required}
      label={t('Criticality')}
      input={
        <Select
          disabled={!!disabled}
          validate={required ? emptyFieldValidation : undefined}
          data-ta="Triggers_Priority"
          name={`${templateString(type, index, nestedAt)}.settings.priority`}
        >
          {[
            { value: 'low', label: t('Low') },
            { value: 'medium', label: t('Medium') },
            { value: 'high', label: t('High') },
          ].map(p => (
            <Select.Option key={p.value} value={p.value}>
              {p.label}
            </Select.Option>
          ))}
        </Select>
      }
    />
  );
};
const serviceTypes = (t: TFunction) => [
  {
    key: 'alert.create',
    label: t('Create MSS alert'),
  },
  {
    key: 'alert.servicenow',
    label: t('Create ServiceNow incident'),
  },
  {
    key: 'alert.servicenow.case',
    label: t('Create ServiceNow case'),
  },
  {
    key: 'email',
    label: t('Send email notification'),
  },
];
export const ActionServiceSelector = ({
  required,
  type,
  index,
  nestedAt,
  disabled,
}: NestedSelectorProps) => {
  const [t] = useTranslation(['configuration', 'common']);
  return (
    <FormGroup
      required={required}
      label={t('Service')}
      input={
        <Select
          disabled={!!disabled}
          validate={required ? emptyFieldValidation : undefined}
          data-ta="Triggers_Service"
          name={`${templateString(type, index, nestedAt)}.settings.service`}
        >
          {serviceTypes(t).map(({ key, label }) => (
            <Select.Option key={key} value={key}>
              {label}
            </Select.Option>
          ))}
        </Select>
      }
    />
  );
};

export const FunctionSelector = ({
  required,
  type,
  index,
  nestedAt,
  disabled,
}: NestedSelectorProps) => {
  const [t] = useTranslation(['configuration', 'common']);
  return (
    <FormGroup
      required={required}
      label={t('Function')}
      input={
        <Select
          disabled={!!disabled}
          validate={required ? emptyFieldValidation : undefined}
          data-ta="Triggers_Function"
          name={`${templateString(type, index, nestedAt)}.settings.function`}
        >
          {[t('SUM'), t('MAX'), t('MIN'), t('AVG')].map(f => (
            <Select.Option key={f} value={f}>
              {f}
            </Select.Option>
          ))}
        </Select>
      }
    />
  );
};

export const AssignedUser = ({
  required,
  type,
  index,
  nestedAt,
  canInherit,
  disabled,
}: NestedSelectorProps) => {
  const { data: custodians } = useCustodianQuery();
  const [t] = useTranslation(['configuration', 'common']);
  const { values, setFieldValue } = useFormikContext();
  const valuePath = `${templateString(
    type,
    index,
    nestedAt
  )}.settings.custodian`;

  useEffect(() => {
    if (canInherit && !get(values, valuePath, null))
      setFieldValue(valuePath, 'empty');
    if (!canInherit && [get(values, valuePath, 'empty')].includes('empty'))
      setFieldValue(
        valuePath,
        get(custodians, '[0].options[0].value', 'empty')
      );
  }, [canInherit, custodians, setFieldValue, valuePath, values]);

  return (
    <FormGroup
      required={required}
      label={t('Assigned user/group')}
      input={
        <CustodianSelect
          disabled={!!disabled}
          validate={required ? emptyFieldValidation : undefined}
          data-ta="Triggers_AssignedUser"
          className="w-full"
          custodians={
            canInherit
              ? [
                  {
                    label: t('Inherit'),
                    options: [
                      {
                        key: 'inherit',
                        value: 'empty',
                        label: t('Use MSS Custodian from trigger'),
                      },
                    ],
                  },
                  ...custodians,
                ]
              : custodians
          }
          name={`${templateString(type, index, nestedAt)}.settings.custodian`}
        />
      }
      error={
        <ErrorMessage
          name={`${templateString(type, index, nestedAt)}.settings.custodian`}
        />
      }
    />
  );
};

export const EntityInput = ({
  required,
  type,
  index,
  nestedAt,
  disabled,
}: NestedSelectorProps) => {
  const [t] = useTranslation(['configuration', 'common']);
  const { entities } = useContext(AutomationsContext);
  const [options, setOptions] = useState<
    { value: string; label: string }[] | undefined
  >([]);

  useEffect(() => {
    const noDuplicates = [...new Set(entities)];
    const entitiesOpValues = noDuplicates?.map(option => ({ value: option }));
    setOptions(entitiesOpValues as any);
  }, [entities]);

  return (
    <FormGroup
      required={required}
      label={t('Entity')}
      input={
        <AutoComplete
          disabled={!!disabled}
          validate={required ? emptyFieldValidation : undefined}
          filterOption={true}
          name={`${templateString(type, index, nestedAt)}.settings.entity`}
          className="w-full"
          options={options || []}
          data-ta="Triggers_Entity"
          allowClear
        />
      }
    />
  );
};

export const MessageInput = ({
  required,
  type,
  index,
  nestedAt,
  disabled,
}: NestedSelectorProps) => {
  const [t] = useTranslation(['configuration', 'common', 'remoteAccess']);
  return (
    <FormGroup
      required={required}
      label={t('Message')}
      input={
        <Input.TextArea
          validate={required ? emptyFieldValidation : undefined}
          disabled={!!disabled}
          data-ta="Triggers_Message"
          name={`${templateString(type, index, nestedAt)}.settings.message`}
        />
      }
    />
  );
};

export const ServiceNowDescriptionInput = ({
  required,
  type,
  index,
  nestedAt,
  disabled,
}: NestedSelectorProps) => {
  const [t] = useTranslation(['configuration', 'common', 'remoteAccess']);

  return (
    <FormGroup
      required={required}
      label={t('Description')}
      input={
        <Input.TextArea
          validate={required ? emptyFieldValidation : undefined}
          disabled={!!disabled}
          data-ta="ServiceNow_Description"
          name={`${templateString(type, index, nestedAt)}.settings.description`}
        />
      }
    />
  );
};

export const ServicenowConfigSelectInput = ({
  required,
  type,
  index,
  nestedAt,
  disabled,
}: NestedSelectorProps) => {
  const { servicenowConfigs } = useContext(AutomationsContext);

  const [t] = useTranslation(['configuration', 'common']);
  const { values, validateForm } = useFormikContext();
  const valuePath = `${templateString(
    type,
    index,
    nestedAt
  )}.settings.servicenow_config_id`;

  return (
    <FormGroup
      required={required}
      label={t('ServiceNow configuration')}
      input={
        <Select
          name={valuePath}
          onChange={validateForm}
          defaultValue={get(values, valuePath, '')}
          disabled={!!disabled}
          validate={required ? emptyFieldValidation : undefined}
        >
          {servicenowConfigs &&
            servicenowConfigs.map((config: { id: string; name: string }) => (
              <Select.Option key={config.id || ''} value={config.id}>
                {config.name}
              </Select.Option>
            ))}
        </Select>
      }
      error={<ErrorMessage name={valuePath} />}
    />
  );
};

export const TagInput = ({
  className,
  ...props
}: React.ComponentProps<typeof AntdInput>) => {
  return (
    <AntdInput className={classNames('w-auto my-2.5', className)} {...props} />
  );
};

export const StyledFlex = ({
  className,
  ...props
}: React.ComponentProps<typeof Flex>) => {
  return (
    <Flex
      className={classNames('mt-2.5 flex-wrap w-full', className)}
      {...props}
    />
  );
};

const P = ({ className, ...props }: React.ComponentProps<'p'>) => {
  return (
    <p
      className={classNames('ml-[5px] m-0 p-0 text-xs', className)}
      {...props}
    />
  );
};

export const StyledTag = ({
  className,
  ...props
}: React.ComponentProps<typeof Tag>) => {
  return (
    <Tag
      className={classNames(
        'flex justify-between items-center ml-0',
        className
      )}
      {...props}
    />
  );
};

export const StyledButton = ({
  className,
  ...props
}: React.ComponentProps<typeof Button>) => {
  return <Button className={classNames('ml-[5px]', className)} {...props} />;
};
export const TriggerUseDefaultEvent = ({
  type,
  index,
  nestedAt,
  disabled,
}: NestedSelectorProps) => {
  const [t] = useTranslation(['configuration', 'common']);

  return (
    <FormGroup
      label={t('Use initial event')}
      input={
        <StyledFlex h="start" v="center">
          <Switch
            disabled={!!disabled}
            size="small"
            name={`${templateString(
              type,
              index,
              nestedAt
            )}.settings.default_empty_event`}
          />
          <P>{t('Add default (empty) event.')}</P>
          <P>
            {t(
              'This will trigger a state change if there is one event or all event values are the same.'
            )}
          </P>
        </StyledFlex>
      }
    />
  );
};

export const EmailInputTags = ({
  type,
  index,
  nestedAt,
  sendToCustodians,
  disabled,
}: NestedSelectorProps) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const [t] = useTranslation(['configuration', 'common']);
  const { values } = useFormikContext<any>();
  const [emailList, setEmailList] = useState<Array<string>>([]);
  const [emailInputValue, setEmailInputValue] = useState('');
  const [error, setError] = useState('');
  const [customReceiverCheck, setCustomReceiverCheck] = useState(false);
  const [, changeCustomReceivers] = useContext(FormValuesContext);

  useEffect(() => {
    if (values?.actions[index!]?.settings.send_custom_email) {
      setCustomReceiverCheck(
        values?.actions[index!]?.settings.send_custom_email
      );
    }
    if (values?.actions[index!]?.settings?.custom_email) {
      setEmailList(values?.actions[index!]?.settings?.custom_email?.split(','));
    }
  }, [values]);

  useEffect(() => {
    changeCustomReceivers({
      index,
      value: emailList.join(','),
    });
  }, [emailList]);

  const handleInputConfirm = () => {
    const isEmail = emailRegex.test(emailInputValue);
    const isSameEmail =
      emailList.filter(item => item === emailInputValue)?.length > 0;

    if (isEmail && !isSameEmail) {
      setEmailList([...emailList, emailInputValue]);
      setEmailInputValue('');
      setError('');
    }

    if (isSameEmail) {
      setError(t('Email is already in the list'));
    }

    if (!isEmail) {
      setError(t('Please provide a valid email address'));
    }
  };

  const deleteEmail = (email: string) => {
    setEmailList(emailList.filter(e => e !== email));
  };

  return (
    <>
      <Grid.Row>
        <Grid.Col width={6}>
          <FormGroup
            label=""
            input={
              <StyledFlex h="start" v="center">
                <Switch
                  disabled={!!disabled}
                  data-ta="send-mail-notification-custodian"
                  size="small"
                  name={`${templateString(
                    type,
                    index,
                    nestedAt
                  )}.settings.send_custodian_email`}
                  onChange={checked =>
                    sendToCustodians ? sendToCustodians(checked) : ''
                  }
                />
                <P>{t('Send email notification to custodian(s)')}</P>
              </StyledFlex>
            }
          />
        </Grid.Col>
        <Grid.Col width={6}>
          <FormGroup
            label=""
            input={
              <StyledFlex h="start" v="center">
                <Switch
                  disabled={!!disabled}
                  data-ta="send-mail-notification-custom"
                  size="small"
                  name={`${templateString(
                    type,
                    index,
                    nestedAt
                  )}.settings.send_custom_email`}
                  onChange={isCheck => {
                    setCustomReceiverCheck(isCheck);
                    if (!isCheck) {
                      setEmailList([]);
                      setError('');
                    }
                  }}
                  checked={customReceiverCheck}
                />
                <P>{t('Send email to custom receiver(s)')}</P>
              </StyledFlex>
            }
          />
        </Grid.Col>
      </Grid.Row>
      {customReceiverCheck && (
        <>
          <StyledFlex>
            {emailList.map(email => (
              <StyledTag
                key={email}
                closable
                onClose={() => deleteEmail(email)}
              >
                {email}
              </StyledTag>
            ))}
          </StyledFlex>
          <TagInput
            disabled={!!disabled}
            type="text"
            size="small"
            value={emailInputValue}
            placeholder={t('Add email address')}
            onChange={value => {
              setEmailInputValue(value.currentTarget.value);
              if (value.currentTarget.value === '') {
                setError('');
              }
            }}
            onPressEnter={e => {
              e.preventDefault();
              handleInputConfirm();
            }}
          />
          <StyledButton
            type="primary"
            size="small"
            icon={<FontAwesomeIcon icon={faPlus} />}
            onClick={handleInputConfirm}
          />
          {error && <p>{error}</p>}
        </>
      )}
    </>
  );
};

export const ServiceNowPayloadInput = ({
  required,
  type,
  index,
  nestedAt,
  disabled,
  subtype,
}: NestedSelectorProps) => {
  const { values } = useFormikContext();
  const { servicenowConfigs } = useContext(AutomationsContext);

  const valueKey = `${templateString(
    type,
    index,
    nestedAt
  )}.settings.servicenow_data`;
  const configKey = `${templateString(
    type,
    index,
    nestedAt
  )}.settings.servicenow_config_id`;
  const jsonSchemaFieldMap: Record<
    'case' | 'incident',
    keyof ServicenowConfig
  > = {
    incident: 'json_schema',
    case: 'json_schema_case',
  };

  const fieldName: keyof ServicenowConfig =
    jsonSchemaFieldMap[subtype || 'incident'];
  const servicenowConfigId = get(values, configKey, null);
  const servicenowConfig = useMemo(
    () =>
      servicenowConfigs
        ? servicenowConfigs.find((x: any) => x.id === servicenowConfigId)
        : null,
    [servicenowConfigId, servicenowConfigs]
  );

  const jsonSchema = useMemo(() => {
    if (
      !servicenowConfig ||
      !servicenowConfig[fieldName] ||
      Object.keys(servicenowConfig[fieldName]).length === 0
    ) {
      return <></>;
    }

    return (
      <Field
        disabled={disabled}
        name={valueKey}
        schema={servicenowConfig[fieldName]}
        component={JsonSchemaFormComponent}
      />
    );
  }, [servicenowConfig, valueKey, fieldName, disabled]);

  return (
    <>
      <FormGroup required={required} label={''} input={jsonSchema} />
    </>
  );
};

export const ValueInput = ({
  required,
  type,
  index,
  nestedAt,
  disabled,
}: NestedSelectorProps) => {
  const [t] = useTranslation(['configuration', 'common']);
  return (
    <FormGroup
      required={required}
      label={t('Value')}
      input={
        <Input
          disabled={!!disabled}
          validate={required ? emptyFieldValidation : undefined}
          data-ta="Triggers_Value"
          name={`${templateString(type, index, nestedAt)}.settings.value`}
        />
      }
    />
  );
};

export const AttributeInput = ({
  required,
  type,
  index,
  nestedAt,
  attributesOptions,
  disabled,
}: NestedSelectorProps) => {
  const [t] = useTranslation(['configuration', 'common']);
  const [options, setOptions] = useState<
    { value: any; label: any }[] | undefined
  >([]);

  useEffect(() => {
    const noDuplicates = [...new Set(attributesOptions)];
    const attributesOpValues = noDuplicates?.map(
      option => ({ value: option }) as any
    );
    setOptions(attributesOpValues);
  }, [attributesOptions]);

  return (
    <FormGroup
      required={required}
      label={t('Attribute')}
      input={
        <AutoComplete
          disabled={!!disabled}
          validate={required ? emptyFieldValidation : undefined}
          name={`${templateString(type, index, nestedAt)}.settings.attribute`}
          className="w-full"
          options={options || []}
          allowClear
        />
      }
    />
  );
};

export const FromInput = ({
  required,
  type,
  index,
  nestedAt,
  disabled,
}: NestedSelectorProps) => {
  const [t] = useTranslation(['configuration', 'common']);
  return (
    <FormGroup
      required={required}
      label={t('From')}
      input={
        <Input
          disabled={!!disabled}
          validate={required ? emptyFieldValidation : undefined}
          data-ta="Triggers_From"
          name={`${templateString(type, index, nestedAt)}.settings.from`}
        />
      }
    />
  );
};
export const ToInput = ({
  required,
  type,
  index,
  nestedAt,
  disabled,
}: NestedSelectorProps) => {
  const [t] = useTranslation(['configuration', 'common']);
  return (
    <FormGroup
      required={required}
      label={t('To')}
      input={
        <Input
          disabled={!!disabled}
          validate={required ? emptyFieldValidation : undefined}
          data-ta="Triggers_To"
          name={`${templateString(type, index, nestedAt)}.settings.to`}
        />
      }
    />
  );
};
export const TitleInput = ({
  required,
  type,
  index,
  nestedAt,
  disabled,
}: NestedSelectorProps) => {
  const [t] = useTranslation(['configuration', 'common']);
  return (
    <FormGroup
      required={required}
      label={t('Title')}
      input={
        <Input
          disabled={!!disabled}
          validate={required ? emptyFieldValidation : undefined}
          data-ta="Triggers_Title"
          name={`${templateString(type, index, nestedAt)}.settings.title`}
        />
      }
    />
  );
};

export const SubjectInput = ({
  required,
  type,
  index,
  nestedAt,
  disabled,
}: NestedSelectorProps) => {
  const [t] = useTranslation(['configuration']);
  return (
    <FormGroup
      required={required}
      label={t('Subject')}
      input={
        <Input
          disabled={!!disabled}
          validate={required ? emptyFieldValidation : undefined}
          data-ta="Actions_Subject"
          name={`${templateString(type, index, nestedAt)}.settings.title`}
        />
      }
    />
  );
};

export const ReceiverInput = ({
  required,
  type,
  index,
  nestedAt,
  canInherit,
  disabled,
}: NestedSelectorProps) => {
  const [t] = useTranslation(['configuration']);
  const { data: custodians } = useCustodianQuery();
  const { values, setFieldValue } = useFormikContext();
  const valuePath = `${templateString(
    type,
    index,
    nestedAt
  )}.settings.custodian`;

  useEffect(() => {
    if (canInherit && !get(values, valuePath, null))
      setFieldValue(valuePath, 'empty');
    if (!canInherit && [get(values, valuePath, 'empty')].includes('empty'))
      setFieldValue(
        valuePath,
        get(custodians, '[0].options[0].value', 'empty')
      );
  }, [canInherit, custodians, setFieldValue, valuePath, values]);

  return (
    <FormGroup
      required={required}
      label={t('Receiver(s)')}
      input={
        <CustodianSelect
          disabled={!!disabled}
          data-ta="Actions_Receiver"
          className="w-full"
          custodians={
            canInherit
              ? [
                  {
                    label: t('Inherit'),
                    options: [
                      {
                        key: 'inherit',
                        value: 'empty',
                        label: t('Use MSS Custodian from trigger'),
                      },
                    ],
                  },
                  ...custodians,
                ]
              : custodians
          }
          name={`${templateString(type, index, nestedAt)}.settings.custodian`}
        />
      }
      error={
        <ErrorMessage
          name={`${templateString(type, index, nestedAt)}.settings.custodian`}
        />
      }
    />
  );
};

const createTimeSelect = (num = 60) => {
  const arr = ['00', '01', '02', '03', '04', '05', '06', '07', '08', '09'];
  for (let i = 10; i < num; i++) {
    arr.push(`${i}`);
  }
  return arr;
};

const HMSToSeconds = ({ hours, minutes, seconds }: any) => {
  const hrs = parseInt(hours) * 60 * 60 || 0;
  const min = parseInt(minutes) * 60 || 0;
  const sec = parseInt(seconds) || 0;

  return hrs + min + sec;
};

const HMSToHourFormat = ({ hours, minutes, seconds }: any) => {
  const hh = `${`${hours}`.length === 1 ? `0${hours}` : hours}`;
  const mm = `${`${minutes}`.length === 1 ? `0${minutes}` : minutes}`;
  const ss = `${`${seconds}`.length === 1 ? `0${seconds}` : seconds}`;

  return moment(`${hh}:${mm}:${ss}`, 'HH:mm:ss').format('HH:mm:ss');
};

type ComponentType =
  | 'TIME'
  | 'STATE_CHANGE'
  | 'STATE_CHANGE_OPERATOR'
  | 'EVENT_RANGE'
  | 'TIME_PATTERN';

export const TimeForInput = ({
  required,
  type,
  index,
  nestedAt,
  valueKey,
  disabled,
}: NestedSelectorProps) => {
  const [t] = useTranslation(['configuration', 'common']);
  const options = createTimeSelect();
  const hours = createTimeSelect(24);
  const { setFieldValue, errors, values, validateForm } =
    useFormikContext<any>();

  // Base temp path
  const valuePath = `${templateString(
    type,
    index,
    nestedAt
  )}.settings.temp.time`;

  // Required for time pattern validation
  const hoursValue = get(values, `${valuePath}.hours`, 0);
  const minutesValue = get(values, `${valuePath}.minutes`, 0);
  const secondsValue = get(values, `${valuePath}.seconds`, 0);

  useEffect(() => {
    validateForm();
  }, [hoursValue, minutesValue, secondsValue, validateForm]);

  const componentType: ComponentType = get(
    values,
    `${templateString(type, index, nestedAt)}.type`
  );

  const setMainValue = (key: string) => (val: string) => {
    /*
    It should format the correct value for the field to send to the backend.
    There are 3 ways the backend can receive from this component.

    1) Time: x Seconds
    2) Time string: HH:MM:SS
    3) Time splitted in 3 fields: hours, minutes and seconds

    Based on the componentType we execute the appropriate formatting and
    timezone calculations.
     */

    // Set the temp value for hour/minute or seconds.
    setFieldValue(`${valuePath}.${key}`, val);

    let calculated: number | string;

    const hours = key === 'hours' ? val : get(values, `${valuePath}.hours`, 0);
    const minutes =
      key === 'minutes' ? val : get(values, `${valuePath}.minutes`, 0);
    const seconds =
      key === 'seconds' ? val : get(values, `${valuePath}.seconds`, 0);

    switch (componentType) {
      case 'TIME':
        // Will calculate timezone aware HH:MM:SS
        calculated = HMSToHourFormat({
          hours: hours,
          minutes: minutes,
          seconds: seconds,
        });

        setFieldValue(
          `${templateString(type, index, nestedAt)}.settings.${valueKey}`,
          calculated
        );

        break;
      case 'STATE_CHANGE':
      case 'STATE_CHANGE_OPERATOR':
      case 'EVENT_RANGE':
        // Will calculate seconds output.
        calculated = HMSToSeconds({
          hours: hours,
          minutes: minutes,
          seconds: seconds,
        });

        setFieldValue(
          `${templateString(type, index, nestedAt)}.settings.${valueKey}`,
          calculated
        );

        break;
      case 'TIME_PATTERN': // Will split into 3 fields: hours, minutes and seconds
        setFieldValue(
          `${templateString(type, index, nestedAt)}.settings.hours`,
          hours
        );
        setFieldValue(
          `${templateString(type, index, nestedAt)}.settings.minutes`,
          minutes
        );
        setFieldValue(
          `${templateString(type, index, nestedAt)}.settings.seconds`,
          seconds
        );

        break;
    }
  };
  const label = {
    TIME: t('Every day at'),
    STATE_CHANGE: t('Duration for'),
    STATE_CHANGE_OPERATOR: t('Duration for'),
    EVENT_RANGE: t('Duration for'),
    TIME_PATTERN: t('Interval'),
  };

  const validationMessage = {
    TIME: t('Time is required'),
    STATE_CHANGE: t('Duration is required'),
    STATE_CHANGE_OPERATOR: t('Duration is required'),
    EVENT_RANGE: t('Duration is required'),
    TIME_PATTERN: t('Interval is required'),
  };

  return (
    <FormGroup
      required={required}
      label={label[componentType]}
      input={
        <>
          <Select
            disabled={!!disabled}
            // It's enough to have this validation only once because we have access to all 3 values
            validate={() =>
              timePatternValidation(
                hoursValue,
                minutesValue,
                secondsValue,
                !!required
              )
            }
            name={`${templateString(
              type,
              index,
              nestedAt
            )}.settings.temp.time.hours`}
            className="w-1/3"
            showSearch
            onChange={setMainValue('hours')}
            placeholder={t('Hours')}
            data-ta="Triggers_For_Hours"
          >
            {hours.map(o => (
              <Select.Option
                data-ta={`hour-option-${o}`}
                value={parseInt(o)}
                key={o}
              >
                {o}
              </Select.Option>
            ))}
          </Select>
          <Select
            disabled={!!disabled}
            validate={required ? emptyFieldValidation : undefined}
            name={`${templateString(
              type,
              index,
              nestedAt
            )}.settings.temp.time.minutes`}
            className="w-1/3"
            showSearch
            onChange={setMainValue('minutes')}
            placeholder={t('Minutes')}
            data-ta="Triggers_For_Minutes"
          >
            {options.map(o => (
              <Select.Option
                data-ta={`minute-option-${o}`}
                value={parseInt(o)}
                key={o}
              >
                {o}
              </Select.Option>
            ))}
          </Select>
          <Select
            disabled={!!disabled}
            validate={required ? emptyFieldValidation : undefined}
            name={`${templateString(
              type,
              index,
              nestedAt
            )}.settings.temp.time.seconds`}
            className="w-1/3"
            showSearch
            onChange={setMainValue('seconds')}
            placeholder={t('Seconds')}
            data-ta="Triggers_For_Seconds"
          >
            {options.map(o => (
              <Select.Option
                data-ta={`second-option-${o}`}
                value={parseInt(o)}
                key={o}
              >
                {o}
              </Select.Option>
            ))}
          </Select>
          {(type === 'trigger' &&
            // @ts-ignore
            errors?.trigger?.settings?.temp?.time?.hours ===
              TIMEPATTERN_ERROR) ||
          (Array.isArray(errors?.conditions) &&
            type === 'condition' &&
            errors?.conditions?.some(
              // @ts-ignore
              x => x?.settings?.temp?.time?.hours === TIMEPATTERN_ERROR
            )) ? (
            <p className="mt-2" style={{ color: '#fa4654' }}>
              {validationMessage[componentType]}
            </p>
          ) : null}
        </>
      }
    />
  );
};
